import React from "react";
import styled from "@emotion/styled";
import { useHistory } from "react-router";
import ServiceWrapper from "../components/ServiceWrapper";
import BizInfo from "../components/BizInfo";
import { useScoreCount } from "../utils/scoreCount";

/**
 * 앱 소개 (2026-10). 안드로이드는 플레이 스토어(10/23부터, 전엔 APK), 아이폰은 '홈 화면에 추가'.
 * 토스페이먼츠 카드사 심사의 'APP 소개 웹 페이지'로도 쓴다. 앱 안에서는 결제를 받지 않는다(결제는 홈페이지).
 * 주소는 /download — 서버에 /app/ 폴더(APK)가 있어서 /app 은 쓰지 않는다.
 */
const PLAY_URL = "https://play.google.com/store/apps/details?id=kr.chordscore.app";

const SHOTS = [
  { src: "/intro/home.jpg", label: "첫 화면" },
  { src: "/intro/score.jpg", label: "마디 보기" },
  { src: "/intro/lyrics.jpg", label: "가사 보기" },
];

const AppIntro: React.FC = () => {
  const history = useHistory();
  const scoreCount = useScoreCount();
  const features = [
    [`${scoreCount}곡이 넘는 코드 악보`, "새로 나온 노래도 계속 올라와요"],
    ["마디 보기 · 가사 보기", "노래방처럼 지금 칠 코드를 따라가요"],
    ["키 바꾸기 · 쉬운 코드", "내 목소리와 손에 맞게 바로 바꿔요"],
    ["반주 · 메트로놈 · 원곡 영상", "혼자서도 합주하듯 연습해요"],
  ];

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <Hero>
          <Badge>코드스코어 앱</Badge>
          <h1>
            치고 싶은 노래,
            <br />
            <em>코드 악보로 바로.</em>
          </h1>
          <p>기타·피아노 코드 악보를 휴대폰에서 더 편하게 보세요.</p>
          <Actions>
            <Primary href={PLAY_URL} target="_blank" rel="noopener noreferrer">
              안드로이드 앱 설치 (구글 플레이)
            </Primary>
            <Secondary href="#iphone">아이폰에서 쓰기</Secondary>
          </Actions>
        </Hero>

        <Phones>
          {SHOTS.map((s) => (
            <figure key={s.src}>
              <Frame>
                <img src={s.src} alt={`코드스코어 앱 ${s.label} 화면`} loading="lazy" />
              </Frame>
              <figcaption>{s.label}</figcaption>
            </figure>
          ))}
        </Phones>

        <Features>
          {features.map(([t, d]) => (
            <li key={t}>
              <strong>{t}</strong>
              <span>{d}</span>
            </li>
          ))}
        </Features>

        <Install>
          <Card>
            <h2>안드로이드폰</h2>
            <Primary href={PLAY_URL} target="_blank" rel="noopener noreferrer">
              구글 플레이에서 설치
            </Primary>
            <ol>
              <li>위 버튼을 눌러 구글 플레이로 가요.</li>
              <li>
                <b>설치</b>를 눌러요.
              </li>
            </ol>
            <small>예전에 파일로 설치한 코드스코어 앱이 있으면 지우고 플레이에서 다시 설치해 주세요. 회원 정보는 그대로예요.</small>
          </Card>
          <Card id="iphone">
            <h2>아이폰</h2>
            <ol>
              <li>
                <b>사파리</b>로 chordscore.com 에 들어와요.
              </li>
              <li>
                화면 아래 <b>공유 버튼</b>(네모에 위쪽 화살표)을 눌러요.
              </li>
              <li>
                <b>홈 화면에 추가</b>를 누르고 오른쪽 위 <b>추가</b>를 눌러요.
              </li>
            </ol>
            <small>홈 화면에 생긴 코드스코어 아이콘으로 앱처럼 쓸 수 있어요.</small>
          </Card>
        </Install>

        <Notice>
          <p>안드로이드 앱은 구글 플레이에 올라와 있어요. 아이폰 앱스토어 등록은 준비하고 있어요.</p>
          <p>
            앱은 홈페이지와 같은 화면이에요. 구독 결제는 앱이 아닌 홈페이지에서만 받아요.{" "}
            <button type="button" onClick={() => history.push("/pricing")}>
              구독 안내 보기
            </button>
          </p>
        </Notice>

        <BizInfo />
      </Page>
    </ServiceWrapper>
  );
};

export default AppIntro;

const AMBER = "#ffb547";

const Page = styled.div`
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 28px 20px 110px;
  display: flex;
  flex-direction: column;
  gap: 40px;
  color: #f3f4f6;
  font-family: "Noto Sans KR", "Apple SD Gothic Neo", sans-serif;
  word-break: keep-all;
  overflow-x: hidden;
  & > * {
    min-width: 0;
  }
  @media (min-width: 700px) {
    padding: 48px 32px 64px;
    overflow-x: visible;
  }
`;

const Hero = styled.section`
  position: relative;
  text-align: center;
  padding: 24px 0 0;
  &::before {
    content: "";
    position: absolute;
    left: 50%;
    top: -40px;
    width: 520px;
    max-width: 100%;
    height: 320px;
    transform: translateX(-50%);
    background: radial-gradient(closest-side, rgba(255, 181, 71, 0.22), rgba(255, 181, 71, 0));
    pointer-events: none;
  }
  & > h1 {
    position: relative;
    margin: 14px 0 12px;
    font-size: 32px;
    line-height: 1.3;
    font-weight: 800;
    & em {
      font-style: normal;
      color: ${AMBER};
    }
    @media (min-width: 700px) {
      font-size: 44px;
    }
  }
  & > p {
    position: relative;
    margin: 0;
    color: #a3a9b5;
    font-size: 16px;
  }
`;

const Badge = styled.span`
  position: relative;
  display: inline-block;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid #3a3220;
  background: #1c1912;
  color: ${AMBER};
  font-size: 13px;
  font-weight: 700;
`;

const Actions = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 24px;
`;

const Primary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  padding: 0 24px;
  border-radius: 14px;
  background: ${AMBER};
  color: #0d0e12;
  font-size: 16px;
  font-weight: 800;
  text-decoration: none;
`;

const Secondary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 52px;
  padding: 0 22px;
  border-radius: 14px;
  border: 1px solid #343842;
  color: #f3f4f6;
  font-size: 16px;
  font-weight: 700;
  text-decoration: none;
`;

const Phones = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  width: calc(100% + 40px);
  box-sizing: border-box;
  margin: 0 -20px;
  padding: 4px 20px 8px;
  & > figure {
    flex: 0 0 62%;
    margin: 0;
    scroll-snap-align: center;
    text-align: center;
  }
  & figcaption {
    margin-top: 10px;
    font-size: 14px;
    color: #a3a9b5;
  }
  @media (min-width: 700px) {
    justify-content: center;
    overflow: visible;
    width: auto;
    margin: 0;
    padding: 0;
    gap: 28px;
    & > figure {
      flex: 0 0 240px;
    }
    & > figure:nth-of-type(2) {
      transform: translateY(-18px);
    }
  }
`;

const Frame = styled.div`
  border-radius: 34px;
  padding: 8px;
  background: #24272f;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.55), inset 0 0 0 1px #3a3e48;
  & > img {
    display: block;
    width: 100%;
    aspect-ratio: 585 / 1266;
    object-fit: cover;
    border-radius: 26px;
    background: #0d0e12;
  }
`;

const Features = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  @media (min-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
  & > li {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 18px 18px;
    border-radius: 16px;
    background: #16181e;
    border: 1px solid #23262e;
  }
  & strong {
    font-size: 17px;
    font-weight: 800;
  }
  & span {
    font-size: 14px;
    color: #a3a9b5;
  }
`;

const Install = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  @media (min-width: 700px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px 20px;
  border-radius: 20px;
  background: #16181e;
  border: 1px solid #3a3220;
  scroll-margin-top: 80px;
  & > h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 800;
  }
  & > ol {
    margin: 0;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 15px;
    line-height: 1.55;
    color: #d5d9e0;
  }
  & b {
    color: #f3f4f6;
  }
  & > small {
    font-size: 13px;
    line-height: 1.5;
    color: #8b919c;
  }
`;

const Notice = styled.div`
  padding: 16px 18px;
  border-radius: 14px;
  background: #1f222a;
  font-size: 14px;
  line-height: 1.6;
  color: #c9ced6;
  & > p {
    margin: 0;
  }
  & > p + p {
    margin-top: 6px;
  }
  & button {
    border: 0;
    padding: 0;
    background: none;
    color: ${AMBER};
    font: inherit;
    font-weight: 700;
    cursor: pointer;
    text-decoration: underline;
  }
`;
